"use client";

import { useEffect, useRef } from "react";

import { getOdometerDigitOffset } from "@/features/home/components/home-rewind/home-rewind.utils";

import { useInViewOnce } from "./useInViewOnce";

const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
const DURATION_MS = 1400;

type FilmOdometerProps = {
  value: number;
  prefix?: string;
  suffix?: string;
  className?: string;
};

/**
 * A number that rolls up like a mechanical counter the first time it is seen.
 * Server HTML shows the final value; screen readers always get the final value.
 */
export const FilmOdometer = ({
  value,
  prefix = "",
  suffix = "",
  className,
}: FilmOdometerProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInViewOnce(ref);
  const digits = String(value).split("");
  const armed = useRef(false);

  // Below the fold at hydration: park the digits at 0 so the roll starts there.
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (element.getBoundingClientRect().top < window.innerHeight) return;
    armed.current = true;
    element.querySelectorAll<HTMLElement>("[data-place]").forEach((column) => {
      column.style.transform = "translateY(0%)";
    });
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element || !inView || !armed.current) return;

    const columns = Array.from(
      element.querySelectorAll<HTMLElement>("[data-place]"),
    );
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION_MS);
      const eased = 1 - (1 - t) ** 4;
      const current = value * eased;
      for (const column of columns) {
        const offset = getOdometerDigitOffset(
          current,
          Number(column.dataset.place),
        );
        column.style.transform = `translateY(-${(offset * 100) / 11}%)`;
      }
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, value]);

  return (
    <span ref={ref} className={className}>
      <span className="sr-only">{`${prefix}${value}${suffix}`}</span>
      <span aria-hidden className="inline-flex tabular-nums">
        {prefix}
        {digits.map((digit, index) => (
          <span
            key={index}
            className="relative inline-block h-[1em] overflow-hidden leading-none"
          >
            <span className="invisible">0</span>
            <span
              data-place={digits.length - 1 - index}
              className="absolute inset-x-0 top-0 flex flex-col"
              style={{
                transform: `translateY(-${(Number(digit) * 100) / 11}%)`,
              }}
            >
              {DIGITS.map((glyph, row) => (
                <span key={row} className="h-[1em]">
                  {glyph}
                </span>
              ))}
            </span>
          </span>
        ))}
        {suffix}
      </span>
    </span>
  );
};
